/**
 * Shapes shared by the native Channel Deep-dive (/channels/:platform), spec
 * docs/superpowers/specs/2026-10-14-native-channel-deep-dive-design.md. Types,
 * the tab order, two guards and the channels with engagement columns; nothing
 * else. NULL is "not measurable", never 0, in every number below.
 *
 * @module services/channels/channelTypes
 */
import type { CreativeFormat } from '@/services/creatives/creativeFormat';
import type { FreshChannel } from '@/services/looker/lookerAppData';
import type {
  BoxState, CurrencyChoice, FactRow, ObjectiveFilter, OverviewWindows, ReadState,
} from '@/services/overview/overviewTypes';
import type { Scorecard } from '@/services/overview/scorecards';
import type { Trend } from '@/services/overview/spendTrend';

/** The ad channels in tab order (spec §4); GA4 follows a divider. Path segments of /channels/:platform. */
export const AD_CHANNELS = ['facebook', 'tiktok', 'google', 'line'] as const;
export type AdChannel = (typeof AD_CHANNELS)[number];
export type ChannelPlatform = AdChannel | 'analytics';
export const CHANNEL_PLATFORMS: readonly ChannelPlatform[] = [...AD_CHANNELS, 'analytics'];

export const isChannelPlatform = (v: string | null | undefined): v is ChannelPlatform =>
  typeof v === 'string' && (CHANNEL_PLATFORMS as readonly string[]).includes(v);
export const isAdChannel = (v: string | null | undefined): v is AdChannel =>
  typeof v === 'string' && (AD_CHANNELS as readonly string[]).includes(v);

/** The channels whose breakdown tables carry Reaction · Comment · Share (D3 spec §5: TikTok's Reaction counts likes). */
export const hasEngagementColumns = (platform: ChannelPlatform): boolean => platform === 'facebook' || platform === 'tiktok';

/** The campaign_status enum, shared by campaigns, ad sets and ads. */
export type EntityStatus = 'active' | 'paused' | 'completed' | 'archived';

// ---------------------------------------------------------------- rows (migration 122)

/**
 * One get_channel_facts row: one day of one ad set, as stored. A FactRow, so
 * D1's totalsOf / trendOf / currencyChoiceOf / scorecardsOf serve D2 unchanged:
 * accountId and accountName are always null (the RPC carries no account) and
 * reach is always null (FR-X-03: never shown).
 */
export interface ChannelFactRow extends FactRow {
  adSetId: string;
  campaignId: string;
  leads: number | null;
  videoViews: number | null;
  engagements: number | null;
  /**
   * Facebook: platform_data.actions (a key absent from an actions object is 0; no actions object is null). TikTok, for
   * reactions, comments and shares only (migration 123): report_metrics likes / comments / shares when a JSON number
   * >= 0, else null. linkClicks stays Facebook-only.
   */
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  linkClicks: number | null;
  /** analytics only: platform_data.sessions. */
  sessions: number | null;
}

/** The two facts reads: the period and the equal period before it (R1). */
export interface ChannelFactPair {
  current: ChannelFactRow[];
  previous: ChannelFactRow[];
}

/** One get_channel_entities row: an ad set with its campaign and account; native ids trimmed, blank = null (M3). */
export interface ChannelEntity {
  workspaceId: string;
  accountId: string | null;
  accountName: string | null;
  accountExternalId: string | null;
  currency: string | null;
  campaignId: string;
  campaignName: string | null;
  campaignExternalId: string | null;
  campaignStatus: EntityStatus | null;
  campaignStartDate: string | null;
  campaignEndDate: string | null;
  /** 'unspecified' when the campaign has none. */
  objective: string;
  adSetId: string;
  adSetName: string | null;
  adSetExternalId: string | null;
  adSetStatus: EntityStatus | null;
  /** The latest date with cost > 0; null = never spent. */
  lastSpendDate: string | null;
}

/** One get_channel_ads row: one ad's totals over the window (ad grain). */
export interface ChannelAdRow {
  workspaceId: string;
  adId: string;
  adSetId: string;
  campaignId: string;
  objective: string;
  currency: string | null;
  adName: string | null;
  /** null = no platform value; creativeFormatOf may still read a format from the ad name. */
  adFormat: string | null;
  adStatus: EntityStatus | null;
  thumbnailUrl: string | null;
  /** Trimmed; a blank one is null (M3). */
  externalAdId: string | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  reactions: number | null;
  comments: number | null;
  shares: number | null;
}

/** One workspace's accounts (R6): Home's freshness, and every platform with an account (analytics included). */
export interface ChannelConnections {
  freshness: FreshChannel[];
  platforms: string[];
}

// ---------------------------------------------------------------- vocabularies

/** Box 5 tabs (spec §3.4), in order. */
export const BREAKDOWN_TABS = ['campaign', 'format', 'targeting', 'creative'] as const;
export type BreakdownTab = (typeof BREAKDOWN_TABS)[number];

/** The type filter's value for a campaign whose segment 2 is no BUY_TYPES entry. */
export const UNCLASSIFIED_TYPE = 'unclassified';

/** Targeting families (spec §4), in chart order. */
export const TARGETING_FAMILIES = ['CORE', 'LAL', 'BROAD', 'RTG', 'UNCLASSIFIED'] as const;
export type TargetingFamily = (typeof TARGETING_FAMILIES)[number];

/** Campaign state (R26). `ended.endDate` null = completed / archived without an end date. */
export type CampaignState =
  | { kind: 'active' }
  | { kind: 'paused' }
  | { kind: 'ended'; endDate: string | null }
  | { kind: 'unknown' };

/** One campaign of the channel, from its first entity row (campaignIndexOf). */
export interface CampaignInfo {
  id: string;
  workspaceId: string;
  name: string | null;
  /** The matched BUY_TYPES entry; null = no type ("ไม่ระบุประเภท"). */
  type: string | null;
  /** classifyCampaignName(name).conforms. */
  conforms: boolean;
  state: CampaignState;
  /** The campaign's external id, shown with a copy button; null = none (blank in the database included), so no ID line. */
  nativeId: string | null;
  accountExternalId: string | null;
}

// ---------------------------------------------------------------- filters

/** D2's filters as the page holds them (URL-backed, spec §4.1). */
export interface ChannelFilterState {
  objective: ObjectiveFilter;
  /** The viewer's pick; null = the default (the most spent). */
  currency: string | null;
  /** BUY_TYPES entries and/or UNCLASSIFIED_TYPE; [] = every type. */
  types: readonly string[];
  /** Campaign ids; [] = every campaign. */
  campaigns: readonly string[];
  /** A. null = not in the URL: on, unless an applied campaign is not Active (R11). */
  active: boolean | null;
  tab: BreakdownTab;
}

export interface TypeOption {
  value: string;
  /** null = no type (worded by the component, "ไม่ระบุประเภท"). */
  label: string | null;
}

export interface CampaignOption {
  value: string;
  name: string;
  /** Set only in all workspaces. */
  workspaceName: string | null;
  active: boolean;
}

export interface FilterOptions {
  types: TypeOption[];
  campaigns: CampaignOption[];
}

/** The filters as applied after validation (R11); [] = not applied. */
export interface AppliedFilters {
  types: string[];
  campaigns: string[];
}

// ---------------------------------------------------------------- box 3 (and GA4)

export type TileMetric =
  | 'reactions' | 'comments' | 'shares' | 'linkClicks' | 'leads' | 'cpl'
  | 'videoViews' | 'viewRate' | 'cpv' | 'conversions' | 'conversionValue' | 'roas'
  | 'cpa' | 'engagements' | 'reach' | 'frequency' | 'videoCompletions'
  | 'sessions' | 'engagedSessions' | 'webConversions' | 'paidShare';

/** 'money4' is money at exactly four decimals: CPV, where two would print ฿0.0094 as ฿0.01 (rules-04 I1). */
export type TileFormat = 'count' | 'money' | 'money4' | 'pct' | 'roas';

/**
 * value = counted (value may still be null: a ratio over 0); notReported = every
 * row NULL, or a static "ไม่ได้รายงาน" tile; none = nothing to count (a conversion
 * tile whose whole period is after the cut) → "—"; notShown = static "v1 ไม่แสดง".
 */
export type TileState = 'value' | 'notReported' | 'none' | 'notShown';

export interface PlatformTile {
  metric: TileMetric;
  format: TileFormat;
  /** Counted through windows.through with the "ตัด 3 วันล่าสุด" chip; Δ compares the cut windows. */
  cut: boolean;
  /** A static tile reads no data. */
  isStatic: boolean;
  state: TileState;
  value: number | null;
  previous: number | null;
  /** null when either side is null or the previous is 0. */
  delta: number | null;
  tone: 'good' | 'bad' | 'neutral';
  /** A ratio tile over a period of fewer than 500 impressions, as box 2's CTR / CPC / CPM (R17): faded with "n<500". */
  lowSample: boolean;
  /**
   * The objective CPA, CPL and ROAS read, which their label names (rules-04 E4, H1): 'conversion' under objective
   * "all", where they read the Conversion objective's rows only; the selected one otherwise. null: every other tile,
   * which reads every row the page's filters keep.
   */
  objective: Exclude<ObjectiveFilter, 'all'> | null;
  /**
   * The objective it names has no current-period row in the page's scope, in any currency, while the page has rows:
   * it reads "—" and says why, "ไม่มีแคมเปญ Conversion ในช่วงนี้" (rules-04 H2). Every other "—" says nothing.
   */
  noObjectiveRows: boolean;
}

/** The rows a tile set reads: the two windows and their cut subsets. */
export interface TileRows {
  current: readonly ChannelFactRow[];
  previous: readonly ChannelFactRow[];
  cut: readonly ChannelFactRow[];
  cutPrevious: readonly ChannelFactRow[];
}

// ---------------------------------------------------------------- box 5

export interface BreakdownMetrics {
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  /** Facebook's and TikTok's columns (TikTok's reactions are likes); null on Google and LINE, or when no row reported them. */
  reactions: number | null;
  comments: number | null;
  shares: number | null;
  /** Raw impressions < 500 (BENCHMARK_MIN_IMPRESSIONS): faded with "n<500". */
  lowSample: boolean;
}

export interface CampaignBreakdownRow extends BreakdownMetrics {
  /** The campaign id. */
  key: string;
  /** The campaign name, else the id's first 8 characters. */
  name: string;
  /** Set only in all workspaces. */
  workspaceName: string | null;
  /** The matched BUY_TYPES entry; null = no type ("ไม่ระบุประเภท"). */
  type: string | null;
  conforms: boolean;
  nativeId: string | null;
  /** ↗; null = the platform's template is not enabled, or an id is missing. */
  adsManagerUrl: string | null;
  state: CampaignState;
}

export interface FormatBreakdownRow extends BreakdownMetrics {
  key: string;
  /** The shared format (creativeFormatOf, D3 §3.4); null = "ไม่ระบุ format". */
  format: CreativeFormat | null;
}

export interface TargetingBreakdownRow extends BreakdownMetrics {
  /** The ad-set id. */
  key: string;
  name: string;
  family: TargetingFamily;
}

export interface CreativeBreakdownRow extends BreakdownMetrics {
  /** The ad id. */
  key: string;
  /** The ad name as stored, '' when none: a blank one is worded by D3's creativeNameOf, which reads externalAdId. */
  name: string;
  /** The platform's own ad id; null = none. */
  externalAdId: string | null;
  thumbnailUrl: string | null;
  /** The shared format, drawn under the name (D3 §5); null = "ไม่ระบุ format". */
  format: CreativeFormat | null;
}

export interface BreakdownTable<R> {
  rows: R[];
  /** "รวม": the same sums over exactly the shown rows. */
  total: BreakdownMetrics;
}

/** Ad-grain honesty (spec §3.4, R17): shares 0–1. */
export interface AdGrainCoverage {
  /** Σ ad spend ÷ Σ ad-set spend under the same filters; null when the facts are not ready or the ad-set spend is null / 0. */
  adShare: number | null;
  /** Spend on ads with a format ÷ Σ ad spend; null when the ad spend is null / 0. */
  formatShare: number | null;
}

export interface AdGrainTable<R> extends BreakdownTable<R> {
  coverage: AdGrainCoverage;
}

export interface SegmentPoint {
  family: TargetingFamily;
  /** Share of spend, 0–1. */
  share: number | null;
  /**
   * null with lowSample: a family under 500 impressions is left out of CTR and CPC. Also null without it when the
   * ratio cannot be formed: CTR with unreported impressions or clicks, CPC with no click or unreported spend.
   */
  ctr: number | null;
  cpc: number | null;
  lowSample: boolean;
}

export interface TargetingBreakdown extends BreakdownTable<TargetingBreakdownRow> {
  /** null on Google: no segment charts (the page says why). */
  segments: SegmentPoint[] | null;
}

export interface NamingCoverage {
  conforming: number;
  withSpend: number;
  /** conforming ÷ withSpend, 0–1; null when no shown campaign spent. */
  share: number | null;
}

export interface BreakdownView {
  /** The effective A, after the URL-campaign rule. */
  activeOnly: boolean;
  /** Non-Active campaigns with rows under the other filters ("ซ่อน N"); null = the facts are not ready. */
  hidden: number | null;
  /** null = the facts are not ready. */
  namingCoverage: NamingCoverage | null;
  campaign: BoxState<BreakdownTable<CampaignBreakdownRow>>;
  format: BoxState<AdGrainTable<FormatBreakdownRow>>;
  targeting: BoxState<TargetingBreakdown>;
  creative: BoxState<AdGrainTable<CreativeBreakdownRow>>;
}

// ---------------------------------------------------------------- GA4

export interface Ga4Point {
  /** The day, or the ISO week's Monday. */
  key: string;
  sessions: number | null;
  webConversions: number | null;
  /** Total ad spend of every ad channel, in the page currency. */
  adSpend: number | null;
}

export interface Ga4View {
  tiles: PlatformTile[];
  grain: 'day' | 'week';
  points: Ga4Point[];
}

// ---------------------------------------------------------------- the page

/** E (spec §5, R7), in place of rows 2–5. lastSpendDate null = unknown (entities loading or failed) or never. */
export type ChannelEmpty =
  | { kind: 'notConnected' }
  | { kind: 'noSpend'; lastSpendDate: string | null };

/**
 * E on D1 and D4 (rule 04.3): the channels in view spent nothing. `channels` = the ticked ids, [] = every channel.
 * notConnected (rules-04 G3) = the one ticked channel is known not to be connected in the one workspace in view
 * (soleChannelConnectedOf), whatever it spent: it comes first, as on D2 (rules-04 H3); unknown keeps noSpend.
 */
export type NoSpendEmpty = { kind: 'noSpend' | 'notConnected'; channels: string[] };

export interface ChannelViewInput {
  platform: ChannelPlatform;
  windows: OverviewWindows;
  facts: ReadState<ChannelFactPair>;
  entities: ReadState<ChannelEntity[]>;
  ads: ReadState<ChannelAdRow[]>;
  /**
   * get_overview_facts over the current window (every ad channel): the GA4 tab's ad-spend line, and D1's currency on
   * an ad tab whose facts spent in more than one currency (rules-04 B4). Idle otherwise.
   */
  adSpend: ReadState<FactRow[]>;
  /** null = unknown (R6). */
  connected: boolean | null;
  filters: ChannelFilterState;
  /** Bangkok yesterday: campaign state (R26). */
  yesterday: string;
  /** Workspace id → name; used only in all workspaces. */
  workspaceNames: Readonly<Record<string, string>>;
  allWorkspaces: boolean;
}

export interface ChannelView {
  /**
   * The page currency (FR-X-02), D1's rule (rules-04 B4): the requested one when the channel spent in it, else D1's
   * (every ad channel's most spent, from adSpend) when the channel spent in it, else the channel's own most spent.
   * From the ads when the facts failed; from adSpend on GA4 (null until it is ready). null while it waits: the facts
   * loading; the facts failed and the ads not ready; adSpend loading for a channel that spent in more than one
   * currency.
   */
  money: CurrencyChoice | null;
  latestFactDate: string | null;
  /** E; null = not shown. */
  empty: ChannelEmpty | null;
  /** The type / campaign choices; failed with the entities (R8). */
  options: BoxState<FilterOptions>;
  applied: AppliedFilters;
  /** "กรองเฉพาะ N แคมเปญ"; null when no type or campaign filter applies. */
  filteredCampaigns: number | null;
  scorecards: BoxState<Scorecard[]>;
  tiles: BoxState<PlatformTile[]>;
  trend: BoxState<Trend>;
  breakdown: BoxState<BreakdownView>;
  /** The GA4 tab's panel; null on an ad channel. */
  ga4: BoxState<Ga4View> | null;
}
